import { createInternalHref } from "shared/createInternalHref";
import { locales } from "shared/locales";
import { RELATED_POSTS_AMOUNT, EN, NL } from "shared/constants";
import sanityClient from "@sanity/client";

const dynamicClient = sanityClient({
  dataset: process.env.NEXT_PUBLIC_SANITY_STUDIO_API_DATASET,
  projectId: process.env.NEXT_PUBLIC_SANITY_STUDIO_API_PROJECT_ID,
  useCdn: true,
});

const staticClient = sanityClient({
  dataset: process.env.NEXT_PUBLIC_SANITY_STUDIO_API_DATASET,
  projectId: process.env.NEXT_PUBLIC_SANITY_STUDIO_API_PROJECT_ID,
  useCdn: false,
});

const previewClient = sanityClient({
  dataset: process.env.NEXT_PUBLIC_SANITY_STUDIO_API_DATASET,
  projectId: process.env.NEXT_PUBLIC_SANITY_STUDIO_API_PROJECT_ID,
  token: process.env.SANITY_PREVIEW_TOKEN,
  useCdn: false,
});

export async function fetchDataForType({
  fields = "{}",
  locale = NL,
  limit = "",
  order = "",
  params = {},
  preview = false,
  query = "",
  type = "",
}) {
  const orderString = order ? `${order}, _updatedAt desc` : `_updatedAt desc`;
  const client = preview ? previewClient : staticClient;

  const typeString = Array.isArray(type)
    ? `_type in [${type.map((x) => `"${x}"`).join(",")}]`
    : `_type == "${type}"`;

  const result = await client.fetch(
    `*[${typeString}${query}] | order(${orderString}) ${fields}${limit}`,
    params
  );

  return localize(result, locale);
}

export async function fetchNavigation({ locale = NL, preview = false }) {
  const client = preview ? previewClient : staticClient;
  const result = await client.fetch(
    `*[_type == "navigation"] {
      items[] {
        ...,
        item-> {
          _type,
          slug,
          type,
        },
        subitems[] {
          ...,
          item-> {
            _type,
            slug,
            type,
          }
        }
      }
    }`,
    {}
  );

  return result.length ? localize(result[0]?.items, locale) : [];
}

export async function fetchFooter({ locale = NL, preview = false }) {
  const client = preview ? previewClient : staticClient;
  const result = await client.fetch(
    `*[_type == "footer"] {
      ...,
      footerMenu[] {
        ...,
        item-> {
          _type,
          slug,
          type,
        }
      },
      menus[] {
        ...,
        items[] {
          ...,
          item-> {
            _type,
            slug,
            type,
          }
        }
      }
    }`,
    {}
  );

  const localizedResult = result.length ? localize(result[0], locale) : {};
  return localizedResult;
}

export async function fetchMirrorPath({ locale = NL, preview, type, slug }) {
  const client = preview ? previewClient : staticClient;
  const mirrorLocale = locale === NL ? EN : NL;

  const types = Array.isArray(type) ? type : [type];
  const typeString = `[${types.map((type) => `"${type}"`).join(",")}]`;

  const result = await client.fetch(
    `*[_type in ${typeString} && (slug.${locale}.current == "${slug}" || slug.current == "${slug}")][0]{ slug }`
  );

  const mirrorPath = result.slug
    ? localize(result, mirrorLocale).slug.current
    : "/";

  return createInternalHref({
    href: mirrorPath,
    locale: mirrorLocale,
    type,
  });
}

export async function fetchSearch({ locale = NL, query }) {
  const types = `["event", "landing", "news", "newsEventsOverview", "personOverview", "projectOverview", "page", "person", "project", "blog"]`;
  const fields = [
    `title.${locale}`,
    `intro.${locale}`,
    `body.${locale}[].children[].text`,
    `name`,
  ];
  const fieldsString = `[${fields.join(",")}]`;

  const groq = `*[_type in ${types} && ${fieldsString} match "*${query}*"] { _type, type, title, intro, slug, name }`;
  const result = await dynamicClient.fetch(groq, {});

  return localize(result, locale);
}

export async function fetchNoContentFallback({ locale = NL, preview, type }) {
  const client = preview ? previewClient : staticClient;
  const result = await client.fetch(
    '*[_type == "noContentFallback" && type == $type] { title, body, type }',
    { type }
  );

  return localize(result, locale);
}

/*
  The localize function localizes all content. It loops over arrays and objects to check if a locale is present
  as a key on an object. It returns the value in its original state with all fields that contain the locale key
  replaced with the value of the provided locale. Note: This assumes there are no fields next to the locale
  key on objects, if there are, those fields are lost.

  const input = {
    title: { nl: 'Dutch', en: 'English', },
    losesFields: { nl: 'Dutch', en: 'English', lostField1: 2, lostField2: 4 }
  }

  const output = localize(input, 'nl') // { title: 'Dutch', losesFields: 'Dutch' }
*/
function localize(value, locale) {
  if (Array.isArray(value)) {
    return value.map((v) => localize(v, locale));
  }

  if (typeof value === "object" && value !== null) {
    const hasLocaleKey =
      locales.map(({ name }) => value[name]).filter(Boolean).length > 0;

    if (hasLocaleKey) {
      return localize(value[locale], locale);
    }

    if (Object.entries(value).length === 0) {
      return null;
    }

    return Object.entries(value).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: localize(value, locale) }),
      {}
    );
  }

  return value;
}

export async function fetchRelatedPosts({ locale, post }) {
  const { related = [], _type, _id } = post;
  const amount = RELATED_POSTS_AMOUNT - related.length;

  const relatedPostsToFill = await fetchDataForType({
    fields: `{
      heroImage {
        ...,
        "url": image.asset->url
      },
      _type,
      date,
      endDate,
      intro,
      publishedAt,
      slug,
      title,
    }`,
    locale,
    limit: `[0...${amount}]`,
    order: "isFeatured desc, date desc, publishedAt desc",
    preview: false,
    query: `&& _id != "${_id}"`,
    type: _type,
  });

  return [...related, ...relatedPostsToFill];
}
